import { api } from './api';
import type {
  ApiResponse,
  AIModelOption,
  AutodevEstimateRecord as AutodevEstimateView,
  AutodevEstimateQuotaSnapshot as AutodevEstimateQuotaView,
} from '@elmes/shared';

export type { AutodevEstimateView, AutodevEstimateQuotaView };

export interface RoutingMetricsSnapshot {
  totalCalls: number;
  fallback: { count: number; rate: number | null; byReason: Record<string, number> };
  toolCount: { p50: number | null; p95: number | null; mean: number | null; samples: number };
  routerDurationMs: { p50: number | null; p95: number | null; mean: number | null; samples: number };
  miscategorized: { total: number; perCallMean: number | null };
  cache: { hitRatio: number | null; readTokens: number; creationTokens: number; reportingCalls: number };
  router: { inputTokens: number; outputTokens: number };
  ringSize: number;
}

// --- Tools ---
export const aiService = {
  async getTools(category?: string) {
    const params = category ? { category } : {};
    const { data } = await api.get<ApiResponse<{ tools: unknown[]; categories: string[] }>>('/ai/tools', { params });
    return data.data!;
  },

  async executeTool(tool: string, params?: Record<string, unknown>) {
    const { data } = await api.post<ApiResponse<unknown>>('/ai/execute', { tool, params });
    return data.data;
  },

  // Confirm or cancel a paused destructive tool (roadmap #48 PR-B).
  // Resolves true if the registry accepted the ack; false if the entry was
  // already resolved or never existed (e.g. server restart, timeout).
  async confirmTool(correlationId: string, confirm: boolean): Promise<boolean> {
    try {
      const { data } = await api.post<ApiResponse<{ correlationId: string; confirmed: boolean }>>(
        `/ai/tool-confirm/${encodeURIComponent(correlationId)}`,
        { confirm },
      );
      return !!data.success;
    } catch {
      return false;
    }
  },

  async getStatus() {
    const { data } = await api.get<ApiResponse<unknown>>('/ai/status');
    return data.data;
  },

  // --- Operational Memory ---
  async listMemory(category?: string) {
    const params = category ? { category } : {};
    const { data } = await api.get<ApiResponse<unknown[]>>('/ai/memory', { params });
    return data.data!;
  },

  async getMemory(key: string) {
    const { data } = await api.get<ApiResponse<unknown>>(`/ai/memory/${encodeURIComponent(key)}`);
    return data.data;
  },

  async setMemory(key: string, value: Record<string, unknown>) {
    const { data } = await api.put<ApiResponse<unknown>>(`/ai/memory/${encodeURIComponent(key)}`, value);
    return data.data;
  },

  async deleteMemory(key: string) {
    await api.delete(`/ai/memory/${encodeURIComponent(key)}`);
  },

  async getMemoryStats() {
    const { data } = await api.get<ApiResponse<unknown>>('/ai/memory/stats');
    return data.data;
  },

  // --- Conversations ---
  async listConversations(status?: string, profileId?: number) {
    const params: Record<string, string | number> = {};
    if (status) params.status = status;
    if (profileId) params.profileId = profileId;
    const { data } = await api.get<ApiResponse<unknown[]>>('/ai/conversations', { params });
    return data.data!;
  },

  async createConversation(title: string, context?: Record<string, unknown>) {
    const { data } = await api.post<ApiResponse<{ id: number; title: string }>>('/ai/conversations', { title, context });
    return data.data!;
  },

  async getConversation(id: number) {
    const { data } = await api.get<ApiResponse<{ conversation: unknown; messages: unknown[] }>>(`/ai/conversations/${id}`);
    return data.data!;
  },

  async addMessage(conversationId: number, role: string, content: string) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/conversations/${conversationId}/messages`, { role, content, tokens_used: content.length });
    return data.data;
  },

  async closeConversation(id: number) {
    const { data } = await api.patch<ApiResponse<unknown>>(`/ai/conversations/${id}/close`);
    return data.data;
  },

  async getConversationStats() {
    const { data } = await api.get<ApiResponse<unknown>>('/ai/conversations/stats');
    return data.data;
  },

  // --- Tool routing metrics (#59 / PR-C) ---
  async getRoutingMetrics() {
    const { data } = await api.get<ApiResponse<RoutingMetricsSnapshot>>('/ai/routing-metrics');
    return data.data;
  },

  async resetRoutingMetrics() {
    const { data } = await api.post<ApiResponse<{ reset: boolean }>>('/ai/routing-metrics/reset');
    return data.data;
  },

  // --- Message Summaries ---
  async getMessageSummary(messageId: number) {
    const { data } = await api.get<ApiResponse<unknown>>(`/ai/messages/${messageId}/summary`);
    return data.data;
  },

  async generateMessageSummary(messageId: number, profileId?: number) {
    const body = profileId ? { profileId } : {};
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/messages/${messageId}/summary`, body);
    return data.data;
  },

  async deleteMessageSummary(messageId: number) {
    await api.delete(`/ai/messages/${messageId}/summary`);
  },

  async batchGenerateSummaries(conversationId: number, profileId?: number) {
    const body = profileId ? { profileId } : {};
    const { data } = await api.post<ApiResponse<{ generated: number; skipped: number; total: number }>>(`/ai/conversations/${conversationId}/summaries/batch`, body);
    return data.data!;
  },

  async getConversationSummaryStats(conversationId: number) {
    const { data } = await api.get<ApiResponse<unknown>>(`/ai/conversations/${conversationId}/summaries/stats`);
    return data.data;
  },

  async submitSummaryFeedback(messageId: number, rating: 'up' | 'down', note?: string) {
    const body: Record<string, unknown> = { rating };
    if (note) body.note = note;
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/messages/${messageId}/summary/feedback`, body);
    return data.data;
  },

  async searchSummaries(query: string, limit?: number) {
    const params: Record<string, string | number> = { q: query };
    if (limit) params.limit = limit;
    const { data } = await api.get<ApiResponse<unknown[]>>('/ai/summaries/search', { params });
    return data.data!;
  },

  async batchSummarizeAll(profileId?: number) {
    const body = profileId ? { profileId } : {};
    const { data } = await api.post<ApiResponse<{ conversationsProcessed: number; generated: number; skipped: number }>>('/ai/summaries/batch-all', body);
    return data.data!;
  },

  // --- Tasks ---
  async listTasks(status?: string) {
    const params = status ? { status } : {};
    const { data } = await api.get<ApiResponse<unknown[]>>('/ai/tasks', { params });
    return data.data!;
  },

  async createTask(task: { type: string; description: string; params?: Record<string, unknown> }) {
    const { data } = await api.post<ApiResponse<unknown>>('/ai/tasks', task);
    return data.data;
  },

  async getTask(id: number) {
    const { data } = await api.get<ApiResponse<unknown>>(`/ai/tasks/${id}`);
    return data.data;
  },

  async updateTaskStatus(id: number, status: string, result?: unknown, errorMessage?: string) {
    const body: Record<string, unknown> = { status };
    if (result !== undefined) body.result = result;
    if (errorMessage) body.error_message = errorMessage;
    const { data } = await api.patch<ApiResponse<unknown>>(`/ai/tasks/${id}/status`, body);
    return data.data;
  },

  async getTaskStats() {
    const { data } = await api.get<ApiResponse<unknown>>('/ai/tasks/stats');
    return data.data;
  },

  // --- Settings ---
  async getSettings() {
    const { data } = await api.get<ApiResponse<AISettings>>('/ai/settings');
    return data.data!;
  },

  async updateSettings(settings: Partial<AISettings>) {
    const { data } = await api.put<ApiResponse<AISettings>>('/ai/settings', settings);
    return data.data!;
  },

  async getCopilotModels(refresh = false) {
    const params = refresh ? { refresh: 'true' } : undefined;
    const { data } = await api.get<ApiResponse<AIModelOption[]>>('/ai/providers/copilot/models', { params });
    return data.data!;
  },

  // --- User Context ---
  async getUserContext() {
    const { data } = await api.get<ApiResponse<UserContextData>>('/ai/context/me');
    return data.data!;
  },

  // --- Profiles ---
  async listProfiles(params?: { page?: number; limit?: number; type?: string; visibility?: string; isActive?: string; isTemplate?: string; search?: string }) {
    const { data } = await api.get<{ success: boolean; data: AIProfile[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>('/ai/profiles', { params });
    return { data: data.data, pagination: data.pagination };
  },

  async getProfile(id: number) {
    const { data } = await api.get<ApiResponse<AIProfile>>(`/ai/profiles/${id}`);
    return data.data!;
  },

  async createProfile(body: Record<string, unknown>) {
    const { data } = await api.post<ApiResponse<AIProfile>>('/ai/profiles', body);
    return data.data!;
  },

  async updateProfile(id: number, body: Record<string, unknown>) {
    const { data } = await api.put<ApiResponse<AIProfile>>(`/ai/profiles/${id}`, body);
    return data.data!;
  },

  async deleteProfile(id: number) {
    const { data } = await api.delete<ApiResponse<{ deleted: boolean }>>(`/ai/profiles/${id}`);
    return data.data!;
  },

  async duplicateProfile(id: number) {
    const { data } = await api.post<ApiResponse<AIProfile>>(`/ai/profiles/${id}/duplicate`);
    return data.data!;
  },

  async createFromTemplate(templateId: number, overrides: Record<string, unknown>) {
    const { data } = await api.post<ApiResponse<AIProfile>>(`/ai/profiles/${templateId}/from-template`, overrides);
    return data.data!;
  },

  async getTemplates(category?: string) {
    const params = category ? { category } : {};
    const { data } = await api.get<ApiResponse<AIProfile[]>>('/ai/profiles/templates', { params });
    return data.data!;
  },

  async getMyProfiles() {
    const { data } = await api.get<ApiResponse<AIProfile[]>>('/ai/profiles/me');
    return data.data!;
  },

  async getProfileWorkspace(id: number) {
    const { data } = await api.get<ApiResponse<{ stats: unknown; files: unknown[] }>>(`/ai/profiles/${id}/workspace`);
    return data.data!;
  },

  async exportProfile(id: number) {
    const { data } = await api.get<ApiResponse<unknown>>(`/ai/profiles/${id}/export`);
    return data.data!;
  },

  async importProfile(jsonBlob: unknown) {
    const { data } = await api.post<ApiResponse<AIProfile>>('/ai/profiles/import', jsonBlob);
    return data.data!;
  },

  async getProfileQuota(id: number) {
    const { data } = await api.get<ApiResponse<ProfileQuota>>(`/ai/profiles/${id}/quota`);
    return data.data!;
  },

  async resetProfileQuota(id: number) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/profiles/${id}/quota/reset`);
    return data.data!;
  },

  // --- Audit Log ---
  async getAuditLog(params?: { limit?: number; agentId?: number }) {
    const { data } = await api.get<ApiResponse<unknown[]>>('/ai/audit', { params });
    return data.data!;
  },

  // --- Issues ---
  async listIssues(params?: Record<string, unknown>) {
    const { data } = await api.get<ApiResponse<unknown[]> & { pagination: unknown }>('/ai/issues', { params });
    return { data: data.data!, pagination: (data as unknown as Record<string, unknown>).pagination };
  },

  async listIssueRevisions(id: number) {
    const { data } = await api.get<ApiResponse<unknown[]>>(`/ai/issues/${id}/revisions`);
    return data.data ?? [];
  },

  async getIssue(id: number) {
    const { data } = await api.get<ApiResponse<unknown>>(`/ai/issues/${id}`);
    return data.data!;
  },

  async createIssue(body: Record<string, unknown>) {
    const { data } = await api.post<ApiResponse<unknown>>('/ai/issues', body);
    return data.data!;
  },

  async updateIssue(id: number, body: Record<string, unknown>) {
    const { data } = await api.put<ApiResponse<unknown>>(`/ai/issues/${id}`, body);
    return data.data!;
  },

  async deleteIssue(id: number) {
    await api.delete(`/ai/issues/${id}`);
  },

  async getIssueStats() {
    const { data } = await api.get<ApiResponse<unknown>>('/ai/issues/stats');
    return data.data!;
  },

  async getDevStats() {
    const { data } = await api.get<ApiResponse<unknown>>('/ai/issues/dev-stats');
    return data.data as
      | { mode: 'sync'; queueEnabled: false }
      | {
          mode: 'queued';
          queueEnabled: true;
          concurrency: number;
          counts: { active: number; waiting: number; delayed: number; completed: number; failed: number };
        };
  },

  async getDlqIssues() {
    const { data } = await api.get<ApiResponse<unknown>>('/ai/issues/dlq');
    return data.data as {
      issues: Array<{
        id: number;
        title: string;
        type: string;
        priority: string;
        status: string;
        devStatus: string | null;
        devAttempt: number | null;
        devError: string | null;
        devBranch: string | null;
        devPrUrl: string | null;
        updatedAt: string | null;
      }>;
      maxAttempts: number;
      count: number;
    };
  },

  async dlqRetry(id: number) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/issues/${id}/dev/dlq-retry`);
    return data.data as { mode: 'sync' | 'queued'; jobId?: string; alreadyEnqueued?: boolean };
  },

  async assignIssue(id: number, body: Record<string, unknown>) {
    const { data } = await api.patch<ApiResponse<unknown>>(`/ai/issues/${id}/assign`, body);
    return data.data!;
  },

  async reviewIssue(id: number, profileId?: number) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/issues/${id}/review`, profileId ? { profileId } : {});
    return data;
  },

  async developIssue(id: number, profileId?: number, force?: boolean) {
    const body: Record<string, unknown> = {};
    if (profileId) body.profileId = profileId;
    if (force) body.force = true;
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/issues/${id}/develop`, body);
    return data;
  },

  async reprocessIssueComment(issueId: number, commentId: number, profileId?: number) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/issues/${issueId}/comments/${commentId}/reprocess`, profileId ? { profileId } : {});
    return data;
  },

  async getIssueDevLogs(issueId: number, attempt?: number) {
    const params: Record<string, unknown> = {};
    if (attempt) params.attempt = attempt;
    const { data } = await api.get<ApiResponse<unknown[]>>(`/ai/issues/${issueId}/dev-logs`, { params });
    return data.data!;
  },

  async getIssueCircuitBreaker(issueId: number) {
    const { data } = await api.get<ApiResponse<{ open: boolean; consecutiveFailures: number; threshold: number; lastResetAt: string | null }>>(
      `/ai/issues/${issueId}/dev/circuit-breaker`,
    );
    return data.data!;
  },

  async resetIssueCircuitBreaker(issueId: number) {
    const { data } = await api.post<ApiResponse<{ open: boolean; consecutiveFailures: number; threshold: number; lastResetAt: string | null }>>(
      `/ai/issues/${issueId}/dev/circuit-breaker/reset`,
    );
    return data.data!;
  },

  // Roadmap #55 — Cost-Aware Estimation
  async createIssueEstimate(issueId: number, profileId?: number) {
    const body: Record<string, unknown> = {};
    if (profileId) body.profileId = profileId;
    const { data } = await api.post<ApiResponse<{ estimate: AutodevEstimateView; quota: AutodevEstimateQuotaView }>>(
      `/ai/issues/${issueId}/estimate`,
      body,
    );
    return data.data!;
  },

  async getIssueEstimate(issueId: number) {
    const { data } = await api.get<ApiResponse<{ estimate: AutodevEstimateView; quota: AutodevEstimateQuotaView } | null>>(
      `/ai/issues/${issueId}/estimate`,
    );
    return data.data ?? null;
  },

  async approveIssueEstimate(issueId: number, estimateId: number) {
    const { data } = await api.post<ApiResponse<{ estimate: AutodevEstimateView; quota: AutodevEstimateQuotaView }>>(
      `/ai/issues/${issueId}/estimate/${estimateId}/approve`,
    );
    return data.data!;
  },

  async rejectIssueEstimate(issueId: number, estimateId: number) {
    const { data } = await api.post<ApiResponse<AutodevEstimateView>>(
      `/ai/issues/${issueId}/estimate/${estimateId}/reject`,
    );
    return data.data!;
  },

  // Issue comments
  async listIssueComments(issueId: number, params?: Record<string, unknown>) {
    const { data } = await api.get<ApiResponse<unknown[]> & { pagination: unknown }>(`/ai/issues/${issueId}/comments`, { params });
    return { data: data.data!, pagination: (data as unknown as Record<string, unknown>).pagination };
  },

  async addIssueComment(issueId: number, body: { content: string; metadata?: Record<string, unknown> }) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/issues/${issueId}/comments`, body);
    return data.data!;
  },

  async updateIssueComment(issueId: number, commentId: number, body: { content: string }) {
    const { data } = await api.put<ApiResponse<unknown>>(`/ai/issues/${issueId}/comments/${commentId}`, body);
    return data.data!;
  },

  async deleteIssueComment(issueId: number, commentId: number) {
    await api.delete(`/ai/issues/${issueId}/comments/${commentId}`);
  },

  // Issue labels
  async listIssueLabels() {
    const { data } = await api.get<ApiResponse<unknown[]>>('/ai/issues/labels');
    return data.data!;
  },

  async createIssueLabel(body: { name: string; color?: string; description?: string }) {
    const { data } = await api.post<ApiResponse<unknown>>('/ai/issues/labels', body);
    return data.data!;
  },

  async deleteIssueLabel(id: number) {
    await api.delete(`/ai/issues/labels/${id}`);
  },

  async addLabelToIssue(issueId: number, labelId: number) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/issues/${issueId}/labels/${labelId}`, {});
    return data.data!;
  },

  async removeLabelFromIssue(issueId: number, labelId: number) {
    await api.delete(`/ai/issues/${issueId}/labels/${labelId}`);
  },

  // PR Operations
  async getIssuePRDiff(issueId: number) {
    const { data } = await api.get<ApiResponse<{ diff: string; stats: string }>>(`/ai/issues/${issueId}/pr/diff`);
    return data.data!;
  },

  async getIssuePRStatus(issueId: number) {
    const { data } = await api.get<ApiResponse<{ state: string; mergeable: string; title: string; additions: number; deletions: number; changedFiles: number; headBranch: string; url: string }>>(`/ai/issues/${issueId}/pr/status`);
    return data.data!;
  },

  async mergeAndDeployIssue(issueId: number) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/issues/${issueId}/pr/merge`);
    return data;
  },
};

export interface AISettings {
  provider: string;
  model: string;
  reasoningEffort: '' | 'low' | 'medium' | 'high' | 'xhigh';
  /** Anthropic extended thinking budget. 0 = disabled. */
  thinkingBudgetTokens: number;
  workingDirectory: string;
  timeout: number;
  maxBuffer: number;
  maxToolRounds: number;
  maxEmptyArgsRetries: number;
  autopilot: boolean;
  maxAutopilotContinues: number;
  allowAllTools: boolean;
  allowAllPaths: boolean;
  outputFormat: 'text' | 'json';
  stream: 'on' | 'off';
  customInstructionsEnabled: boolean;
  systemPrompt: string;
  responseLanguage: string;
  // Summary system
  summaryEnabled: boolean;
  summaryThreshold: number;
  summaryMaxLength: number;
  // Issue review
  issueReviewProfileId: number | null;
  issueAutoCloseRejected?: boolean;
  // Issue auto-development
  issueDevEnabled: boolean;
  issueDevProfileId: number | null;
  autoDevAfterReview?: boolean;
  // Comment review
  commentReviewEnabled?: boolean;
  commentReviewProfileId?: number | null;
  commentRedevCooldownSec?: number;
  commentMaxRedevPerHour?: number;
  // GitHub integration
  githubToken: string;
}

export interface MemoryEntry {
  id: number;
  key: string;
  value: string;
  summary: string | null;
  category: string;
  tags: string | null;
  priority: number;
  estTokens: number;
  createdAt: string;
  updatedAt: string;
}

export interface UserContextData {
  profile: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  } | null;
  mandatoryMemories: MemoryEntry[];
  preferences: MemoryEntry[];
  stats: Record<string, unknown>;
}

export interface AIProfile {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  avatarEmoji: string | null;
  color: string | null;
  type: 'assistant' | 'automation' | 'api';
  createdBy: number | null;
  isSystem: boolean;
  isActive: boolean;
  visibility: 'global' | 'personal';
  settings: Record<string, unknown>;
  permissions: Record<string, unknown>;
  maxDailyTokens: number | null;
  maxDailyConversations: number | null;
  maxConcurrentRequests: number | null;
  dailyTokensUsed: number;
  dailyConversationsUsed: number;
  quotaResetAt: string | null;
  triggers: Record<string, unknown> | null;
  isTemplate: boolean;
  templateCategory: string | null;
  apiKeyHash: string | null;
  hasApiKey: boolean;
  lastUsedAt: string | null;
  conversationCount: number;
  totalTokensUsed: number;
  // Roadmap #41 PR-B (eval flags)
  evalEnabled?: boolean;
  evalJudgeProfileId?: number | null;
  canaryModelAlias?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProfileQuota {
  allowed: boolean;
  reason?: string;
  maxDailyTokens: number | null;
  dailyTokensUsed: number;
  maxDailyConversations: number | null;
  dailyConversationsUsed: number;
  quotaResetAt: string | null;
}
